/**
 * Deterministic synthetic fixtures for the public VAULT demo.
 *
 * These are pure client-side data — nothing is fetched, nothing is written
 * to the database. Rendering the public demo is entirely safe in an
 * incognito window with no cookies.
 *
 * All names, values, dates and identifiers below are FICTIONAL. Card
 * numbers show only the last-4 with the leading digits masked as
 * "•••• ", following VAULT's Privacy Shield UI convention.
 */

const today = () => new Date();
const addDays = (n) => {
    const d = today();
    d.setDate(d.getDate() + n);
    return d.toISOString().slice(0, 10);
};
const iso = (offset) => {
    const d = today();
    d.setDate(d.getDate() + offset);
    return d.toISOString().slice(0, 10);
};

/* ─── PERSONAL — Aarav Mehta ───────────────────────────────────────── */
export const PERSONAL_DEMO = {
    persona: "personal",
    owner: {
        name: "Aarav Mehta",
        email: "aarav.demo@vault.pro",
        currency: "INR",
        avatar_initial: "A",
    },
    brief: {
        headline: "2 payments coming up · 3 subscriptions renewing · 1 policy renewal in 18 days",
        highlights: [
            { tone: "amber",   label: "Payments due",     value: "2",        note: "in the next 11 days" },
            { tone: "coral",   label: "Subs renewing",    value: "3",        note: "this month" },
            { tone: "violet",  label: "Docs to review",   value: "1",        note: "renewal soon" },
            { tone: "emerald", label: "Net worth (est.)", value: "₹42,18,650", note: "assets minus debts" },
        ],
    },
    whats_next: [
        { date: addDays(4),  label: "HDFC Regalia payment", amount: 42680, kind: "payment", tone: "blue"    },
        { date: addDays(11), label: "Axis Atlas payment",    amount: 18450, kind: "payment", tone: "blue"    },
        { date: addDays(7),  label: "Netflix renewal",       amount: 649,   kind: "subscription", tone: "coral" },
        { date: addDays(14), label: "Spotify renewal",       amount: 119,   kind: "subscription", tone: "coral" },
        { date: addDays(18), label: "Health insurance renewal", amount: 24500, kind: "renewal", tone: "amber" },
    ],
    financial_position: {
        assets_total: 5148000,
        liabilities_total: 929350,
        net_worth: 4218650,
        breakdown_assets: [
            { key: "home",         label: "Home",          value: 3600000, tone: "teal"    },
            { key: "vehicle",      label: "Vehicle",       value: 550000,  tone: "teal"    },
            { key: "cash",         label: "Cash & Deposits", value: 320000, tone: "cobalt" },
            { key: "investments",  label: "Investments",   value: 620000,  tone: "violet"  },
            { key: "other",        label: "Other Assets",  value: 58000,   tone: "teal"    },
        ],
        breakdown_liabilities: [
            { key: "home_loan",    label: "Home Loan",     value: 810000, tone: "amber"    },
            { key: "vehicle_loan", label: "Vehicle Loan",  value: 58000,  tone: "amber"    },
            { key: "cc_out",       label: "Credit Card outstanding", value: 61350, tone: "coral" },
        ],
    },
    cards: [
        { id: "c1", bank: "HDFC Bank",        name: "Regalia",      last_4: "4821", credit_limit: 500000, outstanding: 42680, due_day: addDays(4)  },
        { id: "c2", bank: "Axis Bank",        name: "Atlas",        last_4: "7314", credit_limit: 300000, outstanding: 18450, due_day: addDays(11) },
        { id: "c3", bank: "American Express", name: "Platinum Travel", last_4: "2047", credit_limit: 600000, outstanding: 220,   due_day: addDays(19) },
    ],
    subscriptions: [
        { id: "s1", name: "Netflix",             amount: 649,  freq: "Monthly", next: addDays(7),  card_last_4: "4821" },
        { id: "s2", name: "Spotify",             amount: 119,  freq: "Monthly", next: addDays(14), card_last_4: "4821" },
        { id: "s3", name: "iCloud+",             amount: 219,  freq: "Monthly", next: addDays(3),  card_last_4: "2047" },
        { id: "s4", name: "Adobe Creative Cloud", amount: 1675, freq: "Monthly", next: addDays(22), card_last_4: "7314" },
        { id: "s5", name: "Amazon Prime",        amount: 1499, freq: "Yearly",  next: addDays(96), card_last_4: "4821" },
    ],
    expenses_by_category: [
        { category: "Dining",        amount: 12480, tone: "coral"   },
        { category: "Travel",        amount: 24350, tone: "cobalt"  },
        { category: "Shopping",      amount: 18670, tone: "violet"  },
        { category: "Utilities",     amount: 6210,  tone: "amber"   },
        { category: "Groceries",     amount: 9840,  tone: "emerald" },
        { category: "Subscriptions", amount: 3181,  tone: "coral"   },
        { category: "Other",         amount: 4120,  tone: "teal"    },
    ],
    investments: [
        { name: "Mutual Funds",  value: 380000, tone: "violet"  },
        { name: "Equity",        value: 145000, tone: "cobalt"  },
        { name: "Fixed Deposits", value: 80000,  tone: "emerald" },
        { name: "Gold",          value: 15000,  tone: "amber"   },
    ],
    documents: [
        { id: "d1", name: "Health Insurance Policy",   type: "Insurance", renewal: addDays(18), owner: "Aarav" },
        { id: "d2", name: "Home Insurance",            type: "Insurance", renewal: addDays(120), owner: "Aarav" },
        { id: "d3", name: "Vehicle Insurance",         type: "Insurance", renewal: addDays(63),  owner: "Aarav" },
        { id: "d4", name: "Sale deed — 3BHK Andheri",  type: "Property",  renewal: null,          owner: "Aarav" },
        { id: "d5", name: "iPhone 15 Warranty",        type: "Warranty",  renewal: addDays(203),  owner: "Aarav" },
        { id: "d6", name: "ITR AY 2025-26",            type: "Tax",       renewal: null,          owner: "Aarav" },
    ],
};

/* ─── COUPLE — Aarav & Nisha Mehta ─────────────────────────────────── */
export const COUPLE_DEMO = {
    persona: "couple",
    owner: { name: "Aarav Mehta", partner: "Nisha Mehta", currency: "INR" },
    brief: {
        headline: "1 joint bill due · 2 shared subscriptions renewing · Together view up to date",
        highlights: [
            { tone: "amber",   label: "Joint bills due",  value: "1", note: "in 5 days" },
            { tone: "coral",   label: "Shared subs",      value: "2", note: "renewing this month" },
            { tone: "emerald", label: "Joint net worth",  value: "₹68,42,000", note: "combined" },
            { tone: "violet",  label: "Privacy scope",    value: "3 zones", note: "Mine · Partner · Joint" },
        ],
    },
    zones: [
        {
            key: "mine", label: "Mine", owner: "Aarav",
            items: [
                { name: "HDFC Regalia",       amount: 42680, kind: "card" },
                { name: "Adobe Creative Cloud", amount: 1675, kind: "sub" },
                { name: "Health Insurance (Aarav)", amount: 24500, kind: "policy" },
            ],
        },
        {
            key: "partner", label: "Partner", owner: "Nisha",
            items: [
                { name: "ICICI Amazon Pay",   amount: 18820, kind: "card" },
                { name: "Kindle Unlimited",   amount: 169,   kind: "sub" },
                { name: "Health Insurance (Nisha)", amount: 22300, kind: "policy" },
            ],
        },
        {
            key: "joint", label: "Joint", owner: "Both",
            items: [
                { name: "Home Loan EMI",      amount: 47500, kind: "loan" },
                { name: "Electricity",        amount: 3820,  kind: "bill" },
                { name: "Netflix Family",     amount: 899,   kind: "sub" },
                { name: "Home Insurance",     amount: 8600,  kind: "policy" },
                { name: "Property — 3BHK Andheri", amount: 0, kind: "asset" },
            ],
        },
        {
            key: "together", label: "Together", owner: "Household",
            items: [
                { name: "Household calendar (mutual)", amount: 0, kind: "calendar" },
                { name: "Joint bills overview",        amount: 0, kind: "summary"  },
                { name: "Shared documents (12)",       amount: 0, kind: "docs"     },
            ],
        },
    ],
    whats_next: [
        { date: addDays(5),  label: "Electricity bill",         amount: 3820,  kind: "bill", tone: "blue",   scope: "Joint" },
        { date: addDays(9),  label: "Netflix Family renewal",   amount: 899,   kind: "subscription", tone: "coral", scope: "Joint" },
        { date: addDays(12), label: "Home loan EMI",            amount: 47500, kind: "payment", tone: "amber", scope: "Joint" },
        { date: addDays(21), label: "Kindle Unlimited (Nisha)", amount: 169,   kind: "subscription", tone: "coral", scope: "Partner" },
    ],
};

/* ─── FAMILY — Mehta household ─────────────────────────────────────── */
export const FAMILY_DEMO = {
    persona: "family",
    owner: {
        name: "Aarav Mehta",
        members: ["Aarav", "Nisha", "Ishaan (son)", "Riya (daughter)"],
        currency: "INR",
    },
    brief: {
        headline: "1 renewal in 8 days · 2 school documents to review · 4 family members active",
        highlights: [
            { tone: "amber",   label: "Renewals",         value: "3",         note: "in the next 30 days" },
            { tone: "coral",   label: "Family subs",      value: "5",         note: "active" },
            { tone: "emerald", label: "Household worth",  value: "₹82,10,400", note: "consolidated" },
            { tone: "violet",  label: "Family members",   value: "4",         note: "with roles" },
        ],
    },
    members: [
        { name: "Aarav",  role: "Head of household", email: "aarav.demo@vault.pro",  responsibilities: 4 },
        { name: "Nisha",  role: "Co-head",           email: "nisha.demo@vault.pro",  responsibilities: 3 },
        { name: "Ishaan", role: "Family (child)",    email: "—",                     responsibilities: 1 },
        { name: "Riya",   role: "Family (child)",    email: "—",                     responsibilities: 1 },
    ],
    shared_calendar: [
        { date: addDays(8),  label: "Home insurance renewal",     owner: "Aarav", tone: "amber"  },
        { date: addDays(15), label: "Ishaan's school fees",       owner: "Nisha", tone: "cobalt" },
        { date: addDays(22), label: "Family car service",         owner: "Aarav", tone: "teal"   },
        { date: addDays(29), label: "Property document review",   owner: "Joint", tone: "violet" },
    ],
    subscriptions: [
        { name: "Netflix Family",       amount: 899,  freq: "Monthly", owner: "Joint" },
        { name: "Spotify Family",       amount: 179,  freq: "Monthly", owner: "Joint" },
        { name: "iCloud+ 2TB",          amount: 749,  freq: "Monthly", owner: "Aarav" },
        { name: "BYJU'S (Ishaan)",      amount: 1250, freq: "Monthly", owner: "Nisha" },
        { name: "Amazon Prime",         amount: 1499, freq: "Yearly",  owner: "Joint" },
    ],
    documents: [
        { name: "Home Insurance",              type: "Insurance", renewal: addDays(8),   owner: "Aarav" },
        { name: "Family Floater Health",       type: "Insurance", renewal: addDays(41),  owner: "Nisha" },
        { name: "School Insurance — Ishaan",   type: "Education", renewal: addDays(60),  owner: "Nisha" },
        { name: "School ID — Riya",            type: "Education", renewal: null,          owner: "Nisha" },
        { name: "Property — 3BHK Andheri",     type: "Property",  renewal: null,          owner: "Joint" },
        { name: "Vehicle Registration",        type: "Vehicle",   renewal: addDays(180),  owner: "Aarav" },
    ],
    responsibilities: [
        { task: "Home Insurance Renewal",  owner: "Aarav",  status: "Due in 8 days" },
        { task: "School Insurance Docs",   owner: "Nisha",  status: "Review needed" },
        { task: "Property Document Review", owner: "Joint", status: "Due in 29 days" },
        { task: "Family car service",       owner: "Aarav", status: "Scheduled" },
    ],
};

/* ─── BUSINESS OWNER — one life, two ledgers ──────────────────────── */
export const BUSINESS_DEMO = {
    persona: "business",
    owner: { name: "Aarav Mehta", company: "Suntronix Studios LLP", currency: "INR" },
    brief: {
        headline: "1 GST filing in 6 days · 2 vendor invoices due · Personal + Business tracked separately",
        highlights: [
            { tone: "amber",   label: "GST filing",       value: "in 6 days",     note: "GSTR-3B for Feb" },
            { tone: "coral",   label: "Vendor invoices",  value: "2",              note: "due this week" },
            { tone: "emerald", label: "Combined net worth", value: "₹1,12,60,000", note: "Personal + Business" },
            { tone: "violet",  label: "Contexts",         value: "2",              note: "Personal · Business" },
        ],
    },
    /* Two clearly separated worlds, styled like the Couple zones. */
    zones: [
        {
            key: "personal", label: "Personal", owner: "Aarav (personal)",
            items: [
                { name: "HDFC Regalia (personal)", amount: 42680, kind: "card" },
                { name: "Family Floater Health",   amount: 24500, kind: "policy" },
                { name: "Home Loan EMI",           amount: 47500, kind: "loan" },
                { name: "Netflix Family",          amount: 899,   kind: "sub" },
            ],
        },
        {
            key: "business", label: "Business", owner: "Suntronix Studios LLP",
            items: [
                { name: "Axis Business Corporate",  amount: 68400, kind: "card" },
                { name: "AWS Cloud (production)",   amount: 21800, kind: "sub" },
                { name: "Adobe CC (team of 3)",     amount: 5025,  kind: "sub" },
                { name: "Vendor invoice — Studio rent", amount: 45000, kind: "bill" },
                { name: "Vendor invoice — CA fees", amount: 12500, kind: "bill" },
                { name: "GST filing · GSTR-3B",     amount: 0,     kind: "docs" },
                { name: "Business insurance",       amount: 18400, kind: "policy" },
            ],
        },
    ],
    whats_next: [
        { date: addDays(3),  label: "Studio rent vendor invoice", amount: 45000, kind: "bill",  tone: "amber",  scope: "Business" },
        { date: addDays(4),  label: "HDFC Regalia payment (personal)", amount: 42680, kind: "payment", tone: "blue", scope: "Personal" },
        { date: addDays(6),  label: "GST filing · GSTR-3B",       amount: 0,     kind: "filing", tone: "coral",  scope: "Business" },
        { date: addDays(7),  label: "CA fees invoice",            amount: 12500, kind: "bill",  tone: "amber",  scope: "Business" },
        { date: addDays(11), label: "AWS Cloud auto-renewal",     amount: 21800, kind: "subscription", tone: "cobalt", scope: "Business" },
    ],
    documents: [
        { name: "GSTR-3B · Jan 2026",         type: "Tax",       renewal: addDays(6),  owner: "Business" },
        { name: "TDS return · Q3",            type: "Tax",       renewal: addDays(41), owner: "Business" },
        { name: "LLP incorporation deed",     type: "Property",  renewal: null,        owner: "Business" },
        { name: "Business insurance policy",  type: "Insurance", renewal: addDays(88), owner: "Business" },
        { name: "Family Floater Health",      type: "Insurance", renewal: addDays(18), owner: "Personal" },
        { name: "Home Insurance",             type: "Insurance", renewal: addDays(120), owner: "Personal" },
    ],
};

export const DEMO_FIXTURES = {
    personal: PERSONAL_DEMO,
    couple:   COUPLE_DEMO,
    family:   FAMILY_DEMO,
    business: BUSINESS_DEMO,
};

/* Semantic data colours — mirror the Midnight Luxe accent system. */
export const TONE = {
    emerald: { bar: "bg-emerald-500", text: "text-emerald-700", chip: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    cobalt:  { bar: "bg-blue-600",    text: "text-blue-700",    chip: "bg-blue-50 text-blue-700 border-blue-200" },
    blue:    { bar: "bg-sky-500",     text: "text-sky-700",     chip: "bg-sky-50 text-sky-700 border-sky-200" },
    teal:    { bar: "bg-teal-500",    text: "text-teal-700",    chip: "bg-teal-50 text-teal-700 border-teal-200" },
    violet:  { bar: "bg-violet-500",  text: "text-violet-700",  chip: "bg-violet-50 text-violet-700 border-violet-200" },
    coral:   { bar: "bg-rose-500",    text: "text-rose-700",    chip: "bg-rose-50 text-rose-700 border-rose-200" },
    amber:   { bar: "bg-amber-500",   text: "text-amber-700",   chip: "bg-amber-50 text-amber-700 border-amber-200" },
};

export function fmtINR(v) {
    if (v == null || Number.isNaN(Number(v))) return "—";
    return "₹" + Number(v).toLocaleString("en-IN");
}

export function daysFromToday(dateStr) {
    if (!dateStr) return null;
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const then = new Date(dateStr);
    then.setHours(0, 0, 0, 0);
    return Math.round((then - now) / 86400000);
}

export function relativeDate(dateStr) {
    const d = daysFromToday(dateStr);
    if (d == null) return "—";
    if (d === 0) return "Today";
    if (d === 1) return "Tomorrow";
    if (d < 0)  return `${Math.abs(d)}d ago`;
    return `in ${d} days`;
}

// Unused today() helper kept for iso() closure; the below export makes lint happy.
export { iso };
